import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, X } from 'lucide-react';
import { io } from 'socket.io-client';
import { API_BASE } from './lib/runtimeConfig';

const getAppToken = () => { try { return JSON.parse(localStorage.getItem('auth') || '{}').appToken; } catch { return null; } };

const TYPE_ICON = {
    NEW_RESERVATION: '🏠', NEW_MESSAGE: '💬', PAYMENT: '💰',
    MAINTENANCE: '🔧', TASK: '🧹', MANUAL: '📣', SYSTEM: '🔔',
};

const fmtTime = (s) => {
    if (!s) return '';
    const d = new Date(s);
    const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return 'ahora';
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `hace ${diffH} h`;
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ── Audio compartido "desbloqueado" con el primer toque/clic del usuario ─────
// Los navegadores (sobre todo en modo incógnito) mantienen el AudioContext en
// estado "suspended" hasta detectar una interacción real — si creamos uno nuevo
// por cada notificación que llega por socket (sin gesto del usuario), nunca
// suena. Por eso usamos una única instancia compartida que se resume en el
// primer click/touch/tecla de la página y se reutiliza siempre.
let _sharedCtx = null;
const getAudioCtx = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!_sharedCtx) _sharedCtx = new Ctx();
    return _sharedCtx;
};
const unlockAudio = () => { try { getAudioCtx()?.resume(); } catch (_) {} };
if (typeof window !== 'undefined') {
    ['pointerdown', 'touchstart', 'keydown'].forEach(ev =>
        window.addEventListener(ev, unlockAudio, { passive: true }));
}

// Beep corto generado con Web Audio API — no requiere archivo de audio
const playBeep = () => {
    try {
        const ctx = getAudioCtx();
        if (!ctx) return;
        const fire = () => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, ctx.currentTime);
            osc.frequency.setValueAtTime(660, ctx.currentTime + 0.12);
            gain.gain.setValueAtTime(0.18, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
            osc.connect(gain); gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.4);
        };
        if (ctx.state === 'suspended') ctx.resume().then(fire).catch(() => {});
        else fire();
    } catch (_) {}
};

const vibrate = () => { try { navigator.vibrate?.([200, 100, 200]); } catch (_) {} };

/**
 * Campana de notificaciones en tiempo real con sonido + vibración.
 * recipientType: 'ADMIN' | 'TRABAJADOR' | 'CAMARERA'
 * recipientId:   employeeId (requerido para TRABAJADOR/CAMARERA; ignorado para ADMIN)
 */
export default function NotificationBell({ recipientType, recipientId }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [pulse, setPulse] = useState(false);
    const socketRef = useRef(null);
    const boxRef = useRef(null);

    const isAdmin = recipientType === 'ADMIN';
    const baseUrl = isAdmin ? `${API_BASE}/api/notifications/admin` : `${API_BASE}/api/notifications/employee/${recipientId}`;
    const authHeaders = isAdmin ? (() => { const t = getAppToken(); return t ? { Authorization: `Bearer ${t}` } : {}; })() : {};

    const load = useCallback(() => {
        if (!isAdmin && !recipientId) return;
        const url = isAdmin ? baseUrl : `${baseUrl}?type=${recipientType}`;
        fetch(url, { headers: authHeaders })
            .then(r => r.json())
            .then(d => { setItems(d.notifications || []); setUnread(d.unread || 0); })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseUrl, recipientType, recipientId, isAdmin]);

    useEffect(() => { load(); }, [load]);

    // Conexión Socket.io — escucha notificaciones en tiempo real
    useEffect(() => {
        if (!isAdmin && !recipientId) return;
        const room = isAdmin ? 'admin' : (recipientType === 'TRABAJADOR' ? `trabajador:${recipientId}` : `camarera:${recipientId}`);
        const socket = io(API_BASE, { transports: ['websocket', 'polling'] });
        socketRef.current = socket;
        socket.on('connect', () => socket.emit('join', room));
        socket.on('notificacion', (n) => {
            setItems(prev => [n, ...prev].slice(0, 80));
            setUnread(u => u + 1);
            setPulse(true);
            setTimeout(() => setPulse(false), 1800);
            playBeep();
            vibrate();
        });
        return () => { socket.disconnect(); };
    }, [isAdmin, recipientType, recipientId]);

    // Cerrar al hacer clic afuera
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const markAllRead = async () => {
        const url = isAdmin ? `${baseUrl}/read-all` : `${baseUrl}/read-all?type=${recipientType}`;
        try {
            await fetch(url, { method: 'PUT', headers: authHeaders });
            setItems(prev => prev.map(n => ({ ...n, read: true })));
            setUnread(0);
        } catch (_) {}
    };

    const markRead = async (n) => {
        if (n.read) return;
        const url = isAdmin ? `${baseUrl}/${n.id}/read` : `${baseUrl}/${n.id}/read?type=${recipientType}`;
        try {
            await fetch(url, { method: 'PUT', headers: authHeaders });
            setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
            setUnread(u => Math.max(0, u - 1));
        } catch (_) {}
    };

    return (
        <div ref={boxRef} style={{ position: 'relative' }}>
            <button onClick={() => setOpen(o => !o)}
                style={{
                    position: 'relative', width: 40, height: 40, borderRadius: '50%', border: 'none',
                    background: open ? '#003366' : 'rgba(255,255,255,0.12)', color: open ? '#fff' : 'inherit',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: pulse ? 'bell-shake 0.5s ease-in-out' : 'none', transition: 'background 0.2s',
                }}
                title="Notificaciones">
                <Bell size={19} />
                {unread > 0 && (
                    <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
                        {unread > 99 ? '99+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <div style={{ position: 'absolute', top: 48, right: 0, width: 340, maxWidth: '92vw', background: '#fff', borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.18)', overflow: 'hidden', zIndex: 1000, border: '1px solid #e2e8f0' }}>
                    <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>🔔 Notificaciones</span>
                        {unread > 0 && (
                            <button onClick={markAllRead} style={{ border: 'none', background: 'transparent', color: '#003366', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Check size={13}/> Marcar todas
                            </button>
                        )}
                        <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', display: window.innerWidth < 480 ? 'block' : 'none' }}><X size={16}/></button>
                    </div>
                    <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                        {items.length === 0 ? (
                            <div style={{ padding: '36px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                                Sin notificaciones por ahora
                            </div>
                        ) : items.map(n => (
                            <div key={n.id} onClick={() => markRead(n)}
                                style={{ padding: '12px 16px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', background: n.read ? '#fff' : '#eff6ff', display: 'flex', gap: 10 }}>
                                <span style={{ fontSize: 20, flexShrink: 0 }}>{TYPE_ICON[n.type] || '🔔'}</span>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <p style={{ margin: 0, fontWeight: n.read ? 600 : 800, fontSize: 13, color: '#0f172a' }}>{n.title}</p>
                                        {!n.read && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }}/>}
                                    </div>
                                    {n.body && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{n.body}</p>}
                                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#94a3b8' }}>
                                        {n.senderName ? `${n.senderName} · ` : ''}{fmtTime(n.createdAt)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <style>{`
                @keyframes bell-shake {
                    0%, 100% { transform: rotate(0); }
                    20%, 60% { transform: rotate(-15deg); }
                    40%, 80% { transform: rotate(15deg); }
                }
            `}</style>
        </div>
    );
}
